import { Injectable, inject } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IRealtimeService } from '../interfaces/realtime-service.interface';
import { AuthService } from '../auth/auth.service';
import { SessionService } from '../app-state-services/session/session.service';
import { RuntimeConfigService } from '../../../common/services/runtime-config/runtime-config.service';

@Injectable({
  providedIn: 'root',
})
export class SignalRService implements IRealtimeService {
  private hubConnection?: HubConnection;
  private messageSubject = new BehaviorSubject<any>(null);
  private authService = inject(AuthService);
  private sessionService = inject(SessionService);
  private runtimeConfigService = inject(RuntimeConfigService);
  
  public messages$: Observable<any> = this.messageSubject.asObservable();

  constructor() {
    this.runtimeConfigService.config$.subscribe((cfg) => {
      if (cfg.humanAgentFeature && environment.useSignalR) {
        this.connect();
      }
    });
  }

  setAuthToken(token: string): void {
    this.authService.setToken(token);
    console.log('[SignalR] Auth token set');
  }

  getAuthToken(): string | undefined {
    return this.authService.getToken() || undefined;
  }

  connect(): void {
    if (this.isConnected()) return;

    const token = this.getAuthToken();
    
    if (!token) {
      console.log('[SignalR] No authentication token found. Connecting as anonymous user (suitable for chatbot users).');
    } else {
      console.log('[SignalR] Authentication token found. Connecting as authenticated user.');
    }
    
    // Browser WebSockets don't allow custom headers, so dealer/workshop identity is
    // forwarded as query parameters which the backend reads on the SignalR negotiation request.
    const tenantCode = this.sessionService.getTenantCode();
    const workshopId = this.sessionService.getWorkshopId();
    const params = new URLSearchParams();
    if (tenantCode) params.set('tenantCode', tenantCode);
    if (workshopId) params.set('workshopId', workshopId);
    const paramStr = params.toString();
    const signalRUrl = paramStr
      ? `${environment.signalRUrl}${environment.signalRUrl.includes('?') ? '&' : '?'}${paramStr}`
      : environment.signalRUrl;

    this.hubConnection = new HubConnectionBuilder()
      .withUrl(signalRUrl, {
        accessTokenFactory: () => {
          return token || '';
        }
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build();

    this.hubConnection.on('MessageReceived', (data: any) => {
      console.log('SignalR MessageReceived:', data);
      this.messageSubject.next(data);
    });

    this.hubConnection.on('AgentJoined', (data: any) => {
      console.log('SignalR AgentJoined:', data);
      this.messageSubject.next({
        ...data,
        messageSource: 'AGENT_JOINED',
        message: `${data.AgentId} has joined the conversation`
      });
    });

    this.hubConnection.on('AgentLeft', (data: any) => {
      console.log('SignalR AgentLeft:', data);
      this.messageSubject.next({
        ...data,
        messageSource: 'AGENT_LEFT',
        message: `${data.AgentId} has left the conversation`
      });
    });

    this.hubConnection.on('Connected', (data: any) => {
      console.log('SignalR Connected:', data);
    });

    this.hubConnection.on('JoinedConversation', (data: any) => {
      console.log('SignalR JoinedConversation:', data);
    });

    this.hubConnection.on('LeftConversation', (data: any) => {
      console.log('SignalR LeftConversation:', data);
    });

    this.hubConnection.on('UserJoined', (data: any) => {
      console.log('SignalR UserJoined:', data);
    });

    this.hubConnection.on('UserLeft', (data: any) => {
      console.log('SignalR UserLeft:', data);
    });

    this.hubConnection.on('TypingIndicator', (data: any) => {
      console.log('SignalR TypingIndicator:', data);
    });

    this.hubConnection.on('Error', (data: any) => {
      console.error('SignalR Error:', data);
    });

    this.hubConnection.onreconnecting((error) => {
      console.log('SignalR attempting to reconnect:', error);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('SignalR reconnected with ID:', connectionId);
    });

    this.hubConnection.onclose((error) => {
      console.log('SignalR connection closed:', error);
    });

    this.hubConnection.start()
      .then(() => {
        console.log('[SignalR] Connected successfully to:', environment.signalRUrl);
        console.log('[SignalR] Connection ID:', this.hubConnection?.connectionId);
        this.hubConnection?.invoke('TestConnection');
      })
      .catch((error) => {
        console.error('[SignalR] Connection failed:', error);
      });
  }

  disconnect(): void {
    if (this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected) {
      this.hubConnection.stop()
        .then(() => {
          console.log('SignalR disconnected successfully');
        })
        .catch((error) => {
          console.error('Error disconnecting SignalR:', error);
        });
    }
  }

  sendMessage(message: any): void {
    if (this.hubConnection && this.isConnected()) {
      const conversationContextId = message.conversationContextId || '';
      const messageText = message.message || '';
      const messageSource = message.messageSource || 'USER';
      const agentName = message.agentName || '';

      this.hubConnection.invoke('SendMessage', conversationContextId, messageText, messageSource, agentName)
        .then(() => {
          console.log('SignalR message sent:', { conversationContextId, messageText, messageSource, agentName });
        })
        .catch((error) => {
          console.error('Error sending SignalR message:', error);
        });
    } else {
      console.warn('SignalR not connected. Cannot send message:', message);
    }
  }

  isConnected(): boolean {
    return this.hubConnection?.state === HubConnectionState.Connected;
  }

  joinConversation(conversationContextId: string): void {
    console.log('[SignalR] joinConversation called with ID:', conversationContextId);
    console.log('[SignalR] Connection state:', this.hubConnection?.state);
    console.log('[SignalR] Is connected:', this.isConnected());
    
    if (this.hubConnection && this.isConnected()) {
      console.log('[SignalR] Attempting to join conversation:', conversationContextId);
      this.hubConnection.invoke('JoinConversation', conversationContextId)
        .then(() => {
          console.log('[SignalR] Successfully joined conversation:', conversationContextId);
        })
        .catch((error) => {
          console.error('[SignalR] Error joining conversation:', error);
        });
    } else {
      console.warn('[SignalR] Cannot join conversation - not connected. Connection state:', this.hubConnection?.state);
    }
  }

  leaveConversation(conversationContextId: string): void {
    if (this.hubConnection && this.isConnected()) {
      this.hubConnection.invoke('LeaveConversation', conversationContextId)
        .then(() => {
          console.log('Left conversation:', conversationContextId);
        })
        .catch((error) => {
          console.error('Error leaving conversation:', error);
        });
    }
  }

  claimConversation(conversationContextId: string, agentId: string): void {
    if (this.hubConnection && this.isConnected()) {
      this.hubConnection.invoke('ClaimConversation', conversationContextId, agentId)
        .then(() => {
          console.log('Claimed conversation:', conversationContextId, 'by agent:', agentId);
        })
        .catch((error) => {
          console.error('Error claiming conversation:', error);
        });
    }
  }

  releaseConversation(conversationContextId: string, agentId: string): void {
    if (this.hubConnection && this.isConnected()) {
      this.hubConnection.invoke('ReleaseConversation', conversationContextId, agentId)
        .then(() => {
          console.log('Released conversation:', conversationContextId, 'by agent:', agentId);
        })
        .catch((error) => {
          console.error('Error releasing conversation:', error);
        });
    }
  }

  sendTypingIndicator(conversationContextId: string, isTyping: boolean): void {
    if (this.hubConnection && this.isConnected()) {
      this.hubConnection.invoke('SendTypingIndicator', conversationContextId, isTyping)
        .then(() => {
          console.log('Sent typing indicator:', isTyping);
        })
        .catch((error) => {
          console.error('Error sending typing indicator:', error);
        });
    }
  }

  reconnectWithToken(token: string): void {
    this.setAuthToken(token);
    this.disconnect();
    setTimeout(() => {
      this.connect();
    }, 1000);
  }
}